import { useEffect, useState } from "react";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import CapabilitiesChecklist from "@/components/admin/CapabilitiesChecklist";
import DobInput from "@/components/DobInput";
import AddressAutocomplete from "@/components/AddressAutocomplete";

/**
 * Edit-user modal extracted from AdminDashboard.
 *
 * Owns its own save handler so the parent only needs to pass the loaded
 * profile (controlled) and a close/refresh callback.
 */
export default function EditUserModal({
  open,
  editUser,
  editRole,
  setEditRole,
  editProfile,
  setEditProfile,
  loading,
  onClose,
  onSaved,
}) {
  const { user: me } = useAuth();
  const isSuperAdmin = !!me?.is_super_admin;
  // Capabilities state — only meaningful when editing an admin AND
  // viewer is super-admin. Loaded lazily on modal open via the dedicated
  // /admin/users/{id}/capabilities endpoint (so we read both the role +
  // is_super_admin flag of the *target*, not the current viewer).
  const [targetCaps, setTargetCaps] = useState([]);
  const [targetIsSuperAdmin, setTargetIsSuperAdmin] = useState(false);
  const [capsLoading, setCapsLoading] = useState(false);
  // New: per-user "may change own role" flag editable by admins.
  const [canChangeOwnRole, setCanChangeOwnRole] = useState(false);
  const [sendingTestSms, setSendingTestSms] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  // Track whether anything in this modal has been touched since open so the
  // close guard knows to prompt before discarding.
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!open) return;
    setDirty(false);
    setCanChangeOwnRole(!!editUser?.can_change_own_role);
  }, [open, editUser]);
  useEffect(() => {
    if (!open || !editUser || !isSuperAdmin || editProfile?.role !== "admin") return;
    setCapsLoading(true);
    API.get(`/admin/users/${editUser.id}/capabilities`)
      .then(({ data }) => {
        setTargetCaps(data.capabilities || []);
        setTargetIsSuperAdmin(!!data.is_super_admin);
      })
      .catch(() => { setTargetCaps([]); setTargetIsSuperAdmin(false); })
      .finally(() => setCapsLoading(false));
  }, [open, editUser, isSuperAdmin, editProfile?.role]);

  // The shared <DialogContent /> now handles the unsaved-changes guard
  // (auto-detected from `input`/`change` events) so we no longer track
  // a manual `dirty` flag here. `guardedClose` retained as a thin alias
  // for the few places (Cancel button, save success) that close
  // programmatically — those bypass the guard intentionally.
  const guardedClose = () => { onClose?.(); };

  const save = async () => {
    if (!editUser || !editProfile) return;
    try {
      const updates = { name: editProfile.name, email: editProfile.email };
      // Dedicated first/last — admins should populate these so LifeFile
      // outbound stops guessing from `name`. Backend syncs `name` when
      // BOTH are set.
      if (editProfile.first_name !== undefined) updates.first_name = editProfile.first_name;
      if (editProfile.last_name !== undefined) updates.last_name = editProfile.last_name;
      if (editProfile.role !== "patient") {
        updates.role = editRole;
        // Persist the new "Allow self role change" toggle for staff targets.
        updates.can_change_own_role = canChangeOwnRole;
        // Admin can now set contact + practice-address + prescriber
        // licensure fields on any staff record. The backend whitelists
        // these in UserUpdate (May 2026 fix).
        for (const k of [
          "phone", "fax",
          "address_street", "address_city", "address_state", "address_zip",
          "date_of_birth", "gender", "prescriber_type",
          "npi_number", "dea_number", "license_number",
          "supervising_physician_name",
          "sms_new_order_alerts_enabled",
          "email_new_order_alerts_enabled",
          "sms_new_order_alert_template",
          "email_new_order_alert_template",
        ]) {
          if (editProfile[k] !== undefined) updates[k] = editProfile[k];
        }
        if (Array.isArray(editProfile.license_states)) {
          updates.license_states = editProfile.license_states;
        }
      }
      await API.put(`/admin/users/${editUser.id}`, updates);
      // Persist capability changes for admin targets (super-admin only;
      // the backend also enforces this, but skipping the call on the FE
      // saves a doomed round-trip + 403 toast).
      if (
        isSuperAdmin &&
        editProfile.role === "admin" &&
        !targetIsSuperAdmin
      ) {
        await API.put(`/admin/users/${editUser.id}/capabilities`, {
          capabilities: targetCaps,
        }).catch((e) => {
          // Surface but don't roll back the profile update — the user
          // explicitly hit Save knowing the two are coupled.
          toast.error(e?.response?.data?.detail || "Could not save permissions");
        });
      }
      if (editProfile.role === "patient") {
        await API.put("/profile", {
          name: editProfile.name,
          phone: editProfile.phone,
          date_of_birth: editProfile.date_of_birth,
          address_street: editProfile.address_street,
          address_line2: editProfile.address_line2,
          address_city: editProfile.address_city,
          address_state: editProfile.address_state,
          address_zip: editProfile.address_zip,
          address_gate_code: editProfile.address_gate_code,
        }).catch(() => {});
      }
      toast.success("User updated");
      setDirty(false);
      onSaved?.();
    } catch (e) {
      toast.error("Failed to update user");
    }
  };

  const setField = (k, v) => { setDirty(true); setEditProfile((prev) => ({ ...prev, [k]: v })); };
  const setRoleDirty = (v) => { setDirty(true); setEditRole(v); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" dirtyGuardActive={() => dirty}>
        <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Edit User — {editUser?.name}</DialogTitle></DialogHeader>
        {loading && <div className="py-8 text-center text-slate-400 dark:text-slate-500">Loading profile...</div>}
        {!loading && editProfile && (
          <div className="space-y-4" data-testid="edit-user-modal">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">First name</Label>
                <Input
                  value={editProfile.first_name || ""}
                  onChange={(e) => setField("first_name", e.target.value)}
                  placeholder="Sanjay"
                  className="mt-1 rounded-xl"
                  data-testid="edit-user-first-name"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Last name</Label>
                <Input
                  value={editProfile.last_name || ""}
                  onChange={(e) => setField("last_name", e.target.value)}
                  placeholder="Khubchandani"
                  className="mt-1 rounded-xl"
                  data-testid="edit-user-last-name"
                />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 -mt-2">
              First + last are the authoritative fields for ERX, LifeFile, and credentialing. The display name below is auto-synced when both are provided.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Display name</Label>
                <Input value={editProfile.name || ""} onChange={(e) => setField("name", e.target.value)} className="mt-1 rounded-xl" data-testid="edit-user-name" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Email</Label>
                <Input value={editProfile.email || ""} onChange={(e) => setField("email", e.target.value)} className="mt-1 rounded-xl" data-testid="edit-user-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Phone</Label>
                <Input value={editProfile.phone || ""} onChange={(e) => setField("phone", e.target.value)} className="mt-1 rounded-xl" data-testid="edit-user-phone" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Date of Birth</Label>
                <DobInput value={editProfile.date_of_birth || ""} onChange={(iso) => setField("date_of_birth", iso)} className="mt-1" />
              </div>
            </div>
            {editProfile.role !== "patient" && (
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Role</Label>
                <Select value={editRole} onValueChange={setRoleDirty}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prescriber">Prescriber</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="customer_service">Team Member</SelectItem>
                    <SelectItem value="it">Tech Support (Audit &amp; Error Triage)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {editProfile.role !== "patient" && !targetIsSuperAdmin && (
              <div className="rounded-xl border border-purple-200 dark:border-purple-900/40 bg-purple-50 dark:bg-purple-900/10 p-3 flex items-start gap-3" data-testid="self-role-change-toggle">
                <Checkbox
                  id="can-change-own-role"
                  checked={canChangeOwnRole}
                  onCheckedChange={(v) => { setDirty(true); setCanChangeOwnRole(!!v); }}
                  className="mt-0.5"
                  data-testid="can-change-own-role-checkbox"
                />
                <div className="flex-1">
                  <label htmlFor="can-change-own-role" className="text-sm font-medium text-slate-900 dark:text-white cursor-pointer">Allow this user to change their own role</label>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                    When enabled, the user may switch between <b>Customer Service</b>, <b>IT</b>, and <b>Compliance</b> from their own My Profile tab. Never grants access to Admin or Super-Admin.
                  </p>
                </div>
              </div>
            )}
            {editProfile.role === "prescriber" && isSuperAdmin && (
              <div className="rounded-xl border border-indigo-200 dark:border-indigo-900/40 bg-indigo-50 dark:bg-indigo-900/10 p-3 space-y-3" data-testid="sms-new-order-alerts-toggle">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">New-order alerts</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 -mt-2">
                  When a patient submits a new order, this prescriber is notified on the channels you turn on below. Super-admin only — prescribers can't change these themselves.
                </p>

                {/* Quick phone fix — same card so super admin doesn't have to scroll */}
                <div>
                  <Label htmlFor="sms-alert-phone" className="text-xs font-semibold text-slate-700 dark:text-slate-200 inline-flex items-center gap-1">
                    Alert phone number
                    {!editProfile.phone && (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700">missing</span>
                    )}
                  </Label>
                  <Input
                    id="sms-alert-phone"
                    type="tel"
                    inputMode="tel"
                    placeholder="+15125551234"
                    value={editProfile.phone || ""}
                    onChange={(e) => setField("phone", e.target.value)}
                    className="mt-1 rounded-lg bg-white dark:bg-slate-900"
                    data-testid="sms-alert-phone-input"
                  />
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">
                    E.164 preferred. Only used when the SMS toggle is on.
                  </p>
                </div>

                {/* ---- SMS channel ---- */}
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="alert-sms-enabled"
                      checked={!!editProfile.sms_new_order_alerts_enabled}
                      onCheckedChange={(v) => setField("sms_new_order_alerts_enabled", !!v)}
                      className="mt-0.5"
                      data-testid="sms-new-order-alerts-checkbox"
                    />
                    <div className="flex-1">
                      <label htmlFor="alert-sms-enabled" className="text-sm font-medium text-slate-900 dark:text-white cursor-pointer">
                        SMS when a new order arrives
                      </label>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                        Requires a phone number above + a Twilio from-number in the backend.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={sendingTestSms}
                      onClick={async () => {
                        setSendingTestSms(true);
                        try {
                          const { data } = await API.post("/admin/sms/test");
                          toast.success(`Test SMS sent to your number ending ${(data?.to || "").slice(-4)}`);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Test SMS failed");
                        } finally {
                          setSendingTestSms(false);
                        }
                      }}
                      className="h-7 text-[11px] px-2 border-indigo-300 text-indigo-700 hover:bg-indigo-100 shrink-0"
                      data-testid="sms-alert-test-btn"
                      title="Fires one real SMS to YOUR super-admin phone to confirm Twilio delivery."
                    >
                      {sendingTestSms ? "Sending…" : "Send a test text"}
                    </Button>
                  </div>
                  <Label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 mt-2 block">
                    SMS body <span className="text-slate-400 font-normal">(leave blank for the default)</span>
                  </Label>
                  <textarea
                    value={editProfile.sms_new_order_alert_template ?? ""}
                    onChange={(e) => setField("sms_new_order_alert_template", e.target.value)}
                    placeholder="Hi {greeting}, A new order has been submitted for review. Please login to verify!"
                    rows={3}
                    maxLength={400}
                    className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm px-3 py-2"
                    data-testid="sms-alert-template"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    {`{greeting}`} will render as "Dr. Lastname" (or the next best fallback). {(editProfile.sms_new_order_alert_template || "").length}/400 characters.
                  </p>
                  <div className="mt-2">
                    <p className="text-[9px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5">Preview</p>
                    <div className={`rounded-xl border px-3 py-2 text-sm shadow-sm ${editProfile.sms_new_order_alerts_enabled && editProfile.phone
                      ? "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100"
                      : "bg-slate-100 dark:bg-slate-800/60 border-dashed border-slate-300 dark:border-slate-700 text-slate-400 italic"}`}
                      data-testid="sms-alert-preview-bubble">
                      "{renderGreeting(editProfile.sms_new_order_alert_template, buildDrGreeting(editProfile), "Hi {greeting}, A new order has been submitted for review. Please login to verify!")}"
                    </div>
                    {editProfile.sms_new_order_alerts_enabled && !editProfile.phone && (
                      <p className="text-[10px] text-rose-600 mt-1">SMS is on but no phone number is set.</p>
                    )}
                  </div>
                </div>

                {/* ---- Email channel ---- */}
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="alert-email-enabled"
                      checked={!!editProfile.email_new_order_alerts_enabled}
                      onCheckedChange={(v) => setField("email_new_order_alerts_enabled", !!v)}
                      className="mt-0.5"
                      data-testid="email-new-order-alerts-checkbox"
                    />
                    <div className="flex-1">
                      <label htmlFor="alert-email-enabled" className="text-sm font-medium text-slate-900 dark:text-white cursor-pointer">
                        Email when a new order arrives
                      </label>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                        Uses SendGrid — works today, no Twilio number needed.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={sendingTestEmail}
                      onClick={async () => {
                        setSendingTestEmail(true);
                        try {
                          const { data } = await API.post("/admin/email/test");
                          toast.success(`Test email sent to ${data?.to || "your inbox"}`);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Test email failed");
                        } finally {
                          setSendingTestEmail(false);
                        }
                      }}
                      className="h-7 text-[11px] px-2 border-indigo-300 text-indigo-700 hover:bg-indigo-100 shrink-0"
                      data-testid="email-alert-test-btn"
                      title="Fires one real email to YOUR super-admin inbox to confirm SendGrid delivery."
                    >
                      {sendingTestEmail ? "Sending…" : "Send a test email"}
                    </Button>
                  </div>
                  <Label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 mt-2 block">
                    Email body <span className="text-slate-400 font-normal">(leave blank for the default)</span>
                  </Label>
                  <textarea
                    value={editProfile.email_new_order_alert_template ?? ""}
                    onChange={(e) => setField("email_new_order_alert_template", e.target.value)}
                    placeholder={"Hi {greeting},\n\nA new order has been submitted for review. Please login to verify!"}
                    rows={4}
                    maxLength={1500}
                    className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm px-3 py-2"
                    data-testid="email-alert-template"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    Subject is fixed: "New GLPFlo order ready for review". The &quot;Open prescriber dashboard&quot; button is added automatically. {(editProfile.email_new_order_alert_template || "").length}/1500 characters.
                  </p>
                  <div className="mt-2">
                    <p className="text-[9px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5">Preview</p>
                    <div className={`rounded-xl border px-3 py-2 text-xs shadow-sm whitespace-pre-wrap ${editProfile.email_new_order_alerts_enabled && editProfile.email
                      ? "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100"
                      : "bg-slate-100 dark:bg-slate-800/60 border-dashed border-slate-300 dark:border-slate-700 text-slate-400 italic"}`}
                      data-testid="email-alert-preview-bubble">
                      <p className="font-semibold text-slate-900 dark:text-white text-sm">Subject: New GLPFlo order ready for review</p>
                      <p className="mt-1">{renderGreeting(editProfile.email_new_order_alert_template, buildDrGreeting(editProfile), "Hi {greeting},\n\nA new order has been submitted for review. Please login to verify!")}</p>
                      <span className="mt-1.5 inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">Open prescriber dashboard →</span>
                    </div>
                    {editProfile.email_new_order_alerts_enabled && !editProfile.email && (
                      <p className="text-[10px] text-rose-600 mt-1">Email is on but no email address is set on this profile.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            {editProfile.role === "prescriber" && !isSuperAdmin && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-3 text-xs text-slate-500 dark:text-slate-400 space-y-0.5">
                <div>New-order SMS: <span className="font-semibold">{editProfile.sms_new_order_alerts_enabled ? "ON" : "OFF"}</span></div>
                <div>New-order email: <span className="font-semibold">{editProfile.email_new_order_alerts_enabled ? "ON" : "OFF"}</span></div>
                <div className="italic text-[10px]">Super-admin only</div>
              </div>
            )}
            {editProfile.role === "patient" && (
              <div className="bg-slate-50 dark:bg-slate-800/40 rounded-xl p-3 border border-slate-200 dark:border-slate-800">
                <p className="text-xs text-slate-500 dark:text-slate-400">Role: <span className="font-medium text-purple-600">Patient (Customer)</span> — Role is locked for patient accounts</p>
              </div>
            )}
            {editProfile.role === "admin" && isSuperAdmin && !targetIsSuperAdmin && (
              <CapabilitiesChecklist
                value={targetCaps}
                onChange={setTargetCaps}
                disabled={capsLoading}
              />
            )}
            {editProfile.role === "admin" && targetIsSuperAdmin && (
              <div className="rounded-xl border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-amber-800 dark:text-amber-200" data-testid="super-admin-notice">
                This account is a <b>super-admin</b> — it has every capability by default and is not editable here.
              </div>
            )}
            {editProfile.role === "admin" && !isSuperAdmin && (
              <div className="rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-3 text-xs text-slate-600 dark:text-slate-300" data-testid="not-super-admin-notice">
                Only a super-admin can view or edit this admin&apos;s permissions.
              </div>
            )}
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Address</Label>
              <AddressAutocomplete
                value={editProfile.address_street || ""}
                onChange={(val) => setField("address_street", val)}
                onSelect={(s) => {
                  setDirty(true);
                  setEditProfile((prev) => ({
                    ...prev,
                    address_street: s.street || "",
                    address_city: s.city || "",
                    address_state: s.state || "",
                    address_zip: s.zip || "",
                  }));
                }}
                placeholder="Start typing a street address..."
                testId="admin-edit-address-street"
                className="mt-1 rounded-xl"
              />
              <Input value={editProfile.address_line2 || ""} onChange={(e) => setField("address_line2", e.target.value)} placeholder="Apt / Suite / Unit / Lot (optional)" className="mt-2 rounded-xl" data-testid="admin-edit-address-line2" />
              <div className="grid grid-cols-3 gap-2 mt-2">
                <Input value={editProfile.address_city || ""} onChange={(e) => setField("address_city", e.target.value)} placeholder="City" className="rounded-xl" data-testid="admin-edit-address-city" />
                <Input value={editProfile.address_state || ""} onChange={(e) => setField("address_state", e.target.value.toUpperCase())} placeholder="State" className="rounded-xl uppercase" maxLength={2} data-testid="admin-edit-address-state" />
                <Input value={editProfile.address_zip || ""} onChange={(e) => setField("address_zip", e.target.value)} placeholder="ZIP" className="rounded-xl" data-testid="admin-edit-address-zip" />
              </div>
            </div>
            {editProfile.address_gate_code && (
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Gate/Access Code</Label>
                <Input value={editProfile.address_gate_code || ""} onChange={(e) => setField("address_gate_code", e.target.value)} className="mt-1 rounded-xl" />
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={guardedClose}>Cancel</Button>
          <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={save} data-testid="save-user-btn">Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Mirrors services/sms_notifications.notify_prescribers_of_new_order so the
// super-admin sees the EXACT greeting that will go out — prefer last_name,
// then first_name, then the full name field, with "Dr." as the final fallback.
function buildDrGreeting(p) {
  if (!p) return "Dr.";
  const last = (p.last_name || "").trim();
  if (last) return `Dr. ${last}`;
  const first = (p.first_name || "").trim();
  if (first) return first;
  const full = (p.name || "").trim();
  return full || "Dr.";
}

// Render a user-editable template string by substituting `{greeting}`.
// Falls back to `fallback` when the template is blank/null so the preview
// always shows something sensible even on a brand-new prescriber row.
function renderGreeting(template, greeting, fallback) {
  const body = (template && template.trim()) ? template : fallback;
  return body.replace(/\{greeting\}/g, greeting);
}
